import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import ApiService from 'utils/services/ApiService';
import Container from '@mui/material/Container';
import Tooltip, { TooltipProps, tooltipClasses } from '@mui/material/Tooltip';
import { Autocomplete,  TextField} from "@mui/material";
import Text from '../text';
import Button from '../button';
import Line from '../line';
import DropdownSelect from '../drop-down';
import DropdownSelectUsers from '../drop-down/DropDownUsers';
import UserDropdown from '../menu';
import { IGroups } from 'utils/models/groups';
import CONSTANTS from 'utils/constants/UIConstants';
import {
	sendCommand,
	connectToPort,
	responseEmitter,
	disconnectFromPort,
	sendAnalyzerCommand,
	resetConnection,
	getPMandPBMDeviceInfo,
} from '../../utils/usb/USBConnector';
import { styled } from '@mui/material/styles';
import { useConnection } from '../../utils/helper/ConnectionContext';
import { IReduxState } from 'utils/models/redux-states';
import { setSitesInformation } from 'utils/redux/reducers/groupSitesReducer';
import { setGroupsInformation } from 'utils/redux/reducers/groupsReducer';
import { setUpdateReduxData } from 'utils/redux/reducers/updateDataReducer';
import { setCompletedSitesLoading } from 'utils/redux/reducers/completedSitesLoadingReducer';
import { toast } from 'react-toastify';
import USBToast from '../../components/toast/usb';
import LoadingPopup from '../dialog/LoadingBarVerifyDevice';
import { setAllSitesLoaded } from 'utils/redux/reducers/allSitesLoadedReducer';
import { getTestResults, saveTestResults } from 'utils/services/idbService';
import { setAllUsersInCurrentOrgInfo, setViewedUserInfo } from 'utils/redux/reducers/userReducer';
import { User, UserOption } from 'utils/models/user';
import { addEventLog } from 'utils/functions/eventLogFunctions';
import ImportModal from '../import-modal';
import { setHeartbeat } from 'utils/redux/reducers/heartbeatReducer';
import AnalyzerBusyToast from '../../components/toast/analyzerBusy';
import { checkSystemSupport } from 'utils/helper/SystemSupportedContext';

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
	<Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
	[`& .${tooltipClasses.arrow}`]: {
		color: theme.palette.common.white,
	},
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: theme.palette.common.white,
		color: 'rgba(0, 0, 0, 0.87)',
		boxShadow: theme.shadows[1],
		fontSize: 14,
		maxWidth: '444px',
	},
}));

const TooltipTitle = styled(Typography)({
	fontFamily: 'Inter, sans-serif', // Inter font for title
});

const TooltipBody = styled('div')({
	fontFamily: 'Jost, sans-serif', // Jost font for body
});

interface AppNavProps {
	user?: any;
	className?: string;
	onViewedUserUpdate?: (newUserId: number) => void;
	syncedTimeStamps?: string;
	groupsReady?: boolean;
}
const AppNav = ({ 
	user,
	className = "",
	onViewedUserUpdate,
	syncedTimeStamps,
	groupsReady = true,
}: AppNavProps) => {
	const dispatch = useDispatch(); // Get the dispatch function from the useDispatch hook
	const reduxState = useSelector((state: IReduxState) => state.user);
	const reduxGroupsState = useSelector((state: IReduxState) => state.groups);
	const reduxUpdateDataState = useSelector(
		(state: any) => state.updateReduxData
	);
	const isDataUpdated = reduxUpdateDataState.updateReduxData;
	let userData: User | undefined;
	if (typeof reduxState?.userInformation === "string") {
		userData = JSON.parse(reduxState?.userInformation);
	} else {
		userData = reduxState?.userInformation;
	}
	const loginSessionId = useSelector((state: IReduxState) => state.user?.loginSessionId);
	const userId = userData?.id;
	const orgId = userData?.organizationVO?.id;
	const orgName = userData?.organizationVO?.name.replaceAll(" ", "");
	const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
		null
	);
	const [isPBM, setIsPBM] = useState<Boolean>(localStorage.getItem('connectedDeviceType') === 'PBM');
	const isCompletedSiteLoading = useSelector(
		(state: any) => state.completedSitesLoading
	);
	const startHeartbeat = useSelector((state: any) => state.heartbeat?.startHeartbeat);
	const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElUser(event.currentTarget);
	};

	const handleCloseUserMenu = () => {
		setAnchorElUser(null);
	};
	const isAllSitesLoaded = useSelector(
		(state: any) => state.allSitesLoaded
	);
	// const [startHeartbeat, setStartHeartbeat] = useState(false);
	const hasLogged = useRef(false);
	const { isConnected, setIsConnected } = useConnection();
	const [isAnalyzerConnected, setIsAnalyzerConnected] = useState(false);
  	const [isUsbToastVisible, setIsUsbToastVisible] = useState(false);
	const [isImportOpen, setIsImportOpen] = useState(false);
	const [serialNumber, setSerialNumber] = useState(isPBM ? localStorage.getItem('PMSerialNumber') : localStorage.getItem('analyzerSerialNumber'));
	const [secondarySerialNumber, setSecondarySerialNumber] = useState(isPBM ? localStorage.getItem('PBMSerialNumber') : null)
	const [firmwareNumber, setFirmwareNumber] = useState(localStorage.getItem('analyzerFirmwareVersion') ?? 'N/A');
	const siteRestoredRef = useRef(false);
	const hasCheckedSupport = useRef(false);
	const isPBMRef = useRef(isPBM);
	const applyDeviceType = (pbm: boolean) => {
		isPBMRef.current = pbm;
		setIsPBM(pbm);
	};

	const resetDeviceState = () => {
		localStorage.removeItem('connectedDeviceType');
		applyDeviceType(false);
		setIsAnalyzerConnected(false);
		setSerialNumber(null);
		setSecondarySerialNumber(null);
		setFirmwareNumber('N/A');
	};

	useEffect(() => {
		if (!isConnected) resetDeviceState();
	}, [isConnected]);
	

	useEffect(() => {
		if (!userData?.id || !loginSessionId) return;
		checkSystemSupport(userData.id);
	}, [loginSessionId]);
	const handleConnectToPort = async () => {
		const handleData = async (response: any) => {
			const connected = response;
			setIsConnected(connected);
			responseEmitter.removeListener('connection', handleData);
		};
		// Attach listener
		responseEmitter.on('connection', handleData);

		// Send the initial command

		let connected = await connectToPort();

		if (!connected) {
			USBToast("Connection error", "Please check your device and USB port, then try again.", "init-usb-connection-error");
			if (isPBM) {
				const { updatedPMSerial, updatedPBMSerial } = getLatestPMData();
				addEventLog("PBM Connection Failure", 
					"PBM Connection", 
					undefined, undefined, 
					`PM Serial: ${updatedPMSerial}, PBM ID: ${updatedPBMSerial}`
				);
			} else {
				const { updatedSerial, updatedFirmware } = getLatestAnalyzerData();
				addEventLog("Analyzer Connection Failure", 
					"Analyzer Connection", 
					undefined, undefined, 
					`Serial: ${updatedSerial}, Firmware Version: ${updatedFirmware}`
				);
			}
			setIsUsbToastVisible(true);
			setIsConnected(false);
		}
	};


	function openImportModal () {
		setIsImportOpen(true);
	}

	function closeImportModal () {
		setIsImportOpen(false);
	}

	useEffect(() => {
		const sendVerifyCommand = async () => {
			let timeoutId: NodeJS.Timeout | undefined;
			let isUIBusy = false;
			const handleData = async (response: any) => {
				const trimmedResponse =
					typeof response === 'string'
						? response.trim()
						: String(response).trim();

				let label = '';

				//if (trimmedResponse.includes('04DA8')) 
				//	return;
				if (parseGetDeviceInfoCommand(trimmedResponse)) {
					label = 'BCA';
					setIsAnalyzerConnected(true);
				} else if (
					(trimmedResponse.startsWith('0')) || //0 catches the PBM error of 05NBF5 too
					trimmedResponse.startsWith('08') ||
					trimmedResponse.startsWith('B')
				) {
					label = 'PBM';
					setIsAnalyzerConnected(false);
				} else if (trimmedResponse.includes('UI busy')) {
					isUIBusy = true;
				}

				if (label !== '') {
					localStorage.setItem('connectedDeviceType', label);
					setIsPBM(label === 'PBM');
					updateInputValue(true);
					clearTimeout(timeoutId);
					setIsUsbToastVisible(false);
					responseEmitter.removeListener('data', handleData);
					if (label === 'BCA') {
						dispatch(setHeartbeat(true));
						const { updatedSerial, updatedFirmware } = getLatestAnalyzerData();
						addEventLog(
							"Analyzer Connection Success", "Analyzer Connection",
							undefined, undefined,
							`Serial: ${updatedSerial}, Firmware Version: ${updatedFirmware}`
						);
						} else {
						await getPMandPBMDeviceInfo();

						const { updatedPMSerial, updatedPBMSerial } = getLatestPMData();
						addEventLog(
							"PBM Connection Success", "PBM Connection",
							undefined, undefined,
							`PM Serial: ${updatedPMSerial}, PBM ID: ${updatedPBMSerial}`
						);
					}
				}
				
			};

			responseEmitter.on('data', handleData);

			

			await sendAnalyzerCommand('getDeviceInfo\n\r');

			timeoutId = setTimeout(async () => {
				isUIBusy ? toast( <AnalyzerBusyToast />, {
					position: 'bottom-left',
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					className: 'toast-background-color-usb',
				}) : USBToast("Connection error", "Please check your device and USB port, then try again.", "busy-usb-connection-error");
				if(isUIBusy){
					if(isPBM){
						const { updatedPMSerial, updatedPBMSerial } = getLatestPMData();
						addEventLog(
							"PBM Connection Failure", "PBM Connection",
							undefined, undefined,
							`PM Serial: ${updatedPMSerial}, PBM ID: ${updatedPBMSerial}`
						);
					}
					else{
						const { updatedSerial, updatedFirmware } = getLatestAnalyzerData();
						addEventLog(
							"Analyzer Connection Failure", "Analyzer Connection",
							undefined, undefined,
							`Serial: ${updatedSerial}, Firmware Version: ${updatedFirmware}`
						);
					}
				}
				responseEmitter.removeListener('data', handleData);
				setIsUsbToastVisible(true);
				isUIBusy = false;
				try {
					await disconnectFromPort();
					setIsConnected(false);
				} catch (error) {
					console.log('Connection issue::', error);
				}
			}, 500);
		};
		if (isConnected) {
			sendVerifyCommand();
		}
	}, [isConnected]);

	useEffect(() => {

		function heartbeat() {
			if (isConnected && startHeartbeat) {
				sendAnalyzerCommand('getDeviceInfo\n');
			}
		}
		const interval = setInterval(heartbeat, 25000);

		return () => clearInterval(interval);

	}, [startHeartbeat, isConnected])

	function parseGetDeviceInfoCommand(response: string) {
		//PBM never sends commas, so we can determine if this is BCA based on that
		if (response.includes(',')) {

			const parts = response.split(',');
			//format: <serial number>,<firmware version>,<hardware version>,<test kit flavor>
			const [serialNumber, firmwareVersion, hardwareVersion, brandId] = parts;
			let trimmedSerial = serialNumber;
			if (serialNumber.includes('04BA6')) {
				trimmedSerial = serialNumber.substring(5);
			}
			localStorage.setItem('analyzerSerialNumber', trimmedSerial);
			localStorage.setItem('analyzerFirmwareVersion', firmwareVersion);
			localStorage.setItem('analyzerHardwareVersion', hardwareVersion);
			localStorage.setItem('analyzerBrandId', brandId);
			return true;

		} else {
			return false;
		}
	}
	const getLatestAnalyzerData = () => {
		const updatedSerial = localStorage.getItem('analyzerSerialNumber') ?? 'N/A';
		const updatedFirmware = localStorage.getItem('analyzerFirmwareVersion') ?? 'N/A';
		if(!isPBM){
			setSerialNumber(updatedSerial);
			setFirmwareNumber(updatedFirmware);
		}
		return { updatedSerial, updatedFirmware };
	};
	const getLatestPMData = () => {
		
		const updatedPMSerial = localStorage.getItem('PMSerialNumber') ?? 'N/A';
		const updatedPBMSerial = localStorage.getItem('PBMSerialNumber') ?? 'N/A';
		if(isPBM){
			setSerialNumber(updatedPMSerial);
			setSecondarySerialNumber(updatedPBMSerial);
		}
		return { updatedPMSerial, updatedPBMSerial };
	};
	const updateInputValue = async (val: boolean) => {
		if (!isConnected) {
			setIsConnected(val);
		}
	};

	const [groups, setGroups] = useState<IGroups[]>();
	const [sites, setSites] = useState<any[]>([]);
	const [selectedSite, setSelectedSite] = useState<any>(null);
	const [userOptions, setUserOptions] = useState<any[]>([{
		id: 0,
		fullName: 'LOADING',
		email: "",
	}]);
	const [selectedUser, setSelectedUser] = useState<any>({
		id: 0,
		fullName: 'LOADING',
		email: "",
	});
	const [groupOptions, setGroupOptions] = useState<any[]>(['LOADING']);
	const [selectedGroup, setSelectedGroup] = useState(() => {
		return localStorage.getItem('selectedGroup') ?? 'LOADING';
	});
	const [viewedUserId, setViewedUserId] = useState<number>(0);

	// Add the groups to the dropdown by adding names to a list
	const renderGroupOptions = (data: any) => {
		const result = [];
		for (const group of data) {
			let groupName = group.name;
			if (groupName === 'Ungrouped') {
				groupName = 'TEST+ANALYZE SITES';
			}
			if (groupName !== 'ALL SITES') {
				result.push(groupName);
			}
		}
		result.push('ALL SITES');
		setGroupOptions(result);

		const savedGroup = localStorage.getItem('selectedGroup');
		if (savedGroup && savedGroup !== 'ALL SITES' && !result.includes(savedGroup)) {
			localStorage.setItem('selectedGroup', 'ALL SITES');
			setSelectedGroup('ALL SITES');
			handleChangeGroup('ALL SITES');
		}
	};

	function adjustFullName(firstName: string, lastName: string, email: string) {
		let fullName = lastName + ", " + firstName;
		if ((lastName + firstName).trim() === '') {
			fullName = email;
		}
		return fullName;
	}

	// Add the users to the dropdown by adding names to a list
	const renderUserOptions = (data: any) => {
		const result = [] as UserOption[];
		let loggedUser = {
			id: userData?.id ?? 0,
			fullName: adjustFullName((userData?.firstName ?? ""), (userData?.lastName ?? ""), (userData?.email ?? "")),
			email: userData?.email ?? "",
		}
		result.push(loggedUser);

		for (const user of data) {
			let incomingUser = {
				id: user.id,
				fullName: adjustFullName(user.firstName, user.lastName, user.email),
				email: user.email,
			}
			if (incomingUser.id !== loggedUser.id) {
				result.push(incomingUser);
			}
		}
		setUserOptions(result);
	}

	function handleChangeUser(selectedValue: any) {

		setSelectedUser(
			{ id: selectedValue }
		);

		setViewedUserId(selectedValue);
		getGroups(selectedValue);
		//put a flag in the redux to indicate a viewed user state. 0 means current user.
		dispatch(setViewedUserInfo(selectedValue === userData?.id ? 0 : selectedValue));

		if (onViewedUserUpdate) {
			onViewedUserUpdate(selectedValue);
		}
	}

	const getSelectedGroup = (selectedGroup: string) => {
		if (groups) {
			for (const group of groups) {
				if (selectedGroup === group.name) {
					return group;
				}
			}
		}
	};

	const handleChangeGroup = (selectedValue: string) => {
		const internalGroupName = selectedValue;

		setSelectedGroup(selectedValue);
		setSelectedSite(null);
		addEventLog("Group Filter Updated", 
			"Group Filter", 
			undefined, undefined, 
			`Group: ${internalGroupName}`
		);
		if (internalGroupName !== 'ALL SITES') {
			dispatch(setSitesInformation(getSelectedGroup(internalGroupName)));
		} else {
			dispatch(setSitesInformation({ name: selectedValue }));
		}
		localStorage.setItem('selectedGroup', selectedValue);
	};

	const handleSiteChange = (site: any) => {
		if (!site) return;
		setSelectedSite(site);
		addEventLog("Site Filter Updated", 
			"Site Filter", 
			undefined, undefined, 
			`Site: ${site.name}`
		);
		dispatch(setSitesInformation({ name: site.name, sites: [site] }));
		localStorage.setItem('selectedSite', site.name ?? '');
	};
	const handleSiteClear = () => {
		setSelectedSite(null);
		if (selectedGroup !== 'ALL SITES') {
			dispatch(setSitesInformation(getSelectedGroup(selectedGroup)));
		} else {
			dispatch(setSitesInformation({ name: 'ALL SITES' }));
		}
		localStorage.removeItem('selectedSite');
	};

	useEffect(() => {
		if (selectedGroup === 'LOADING') {
			dispatch(setCompletedSitesLoading(false));
			dispatch(setAllSitesLoaded(false));
		} else {
			setTimeout(() => {
				dispatch(setCompletedSitesLoading(true));
				dispatch(setAllSitesLoaded(true));
			}, 2000);
		}
	}, [selectedGroup, dispatch]);

	const getAllUsers = async () => {
		if(!navigator.onLine || !orgId || !userData?.userPermissionVOs || userData?.userPermissionVOs[0]?.level === 3) return;
		try {
			const result = await ApiService.getAllUsersOfOrg(orgId, {
				pageNumber: 1,
				pageSize: 0,
				sortPropertyIds: ['lastName', 'firstName'],
				sortStates: [false, false]
			});
			if (result.status !== 200)
				throw Error('Error connecting with the server.');

			renderUserOptions(result.data);
			dispatch(setAllUsersInCurrentOrgInfo(result.data));
			if(selectedUser.fullName === 'LOADING'){
				setSelectedUser({
					id: userData?.id ?? 0,
					fullName: adjustFullName((userData?.firstName ?? ""), (userData?.lastName ?? ""), (userData?.email ?? "")),
					email: userData?.email ?? "",
				});
			}
		} catch (error) {
			console.log(error);
		}
	};

	const getFilteredSites = () => {
		if (!sites.length) return [];
		if (selectedGroup === 'ALL SITES') return sites;
		const groupData = getSelectedGroup(selectedGroup);
		if (groupData?.sites) {
			return [...groupData.sites].sort((a, b) => a.sortOrder - b.sortOrder);
		}
		return [];
	};
	const deduplicateSites = (sites: any) => {
		const seen = new Map();
		for (const site of sites) {
			const key = site.siteId ?? site.id ?? site.name;
			if (!seen.has(key)) {
				seen.set(key, site);
			}
		}
		return Array.from(seen.values());
	};
	// Get all groups by userID
	const getGroups = async (userIdParam = 0) => {
		if (!groupsReady) return;
		if (userId && navigator.onLine) {
			try {
				let userIdToSend = userId;
				if (userIdParam !== 0) {
					userIdToSend = userIdParam;
				}
				const result = await ApiService.getUserGroups(userIdToSend);
				if (result.status !== 200)
					throw Error('Error connecting with the server.');
				const sites = deduplicateSites(
					result.data.flatMap((group: any) => group.sites ?? [])
				);

				setGroups(result.data);
				setSites(sites);
				renderGroupOptions(result.data);
				dispatch(setGroupsInformation(result.data));
				if (selectedSite) {
					dispatch(setSitesInformation({ name: selectedSite.name, sites: [selectedSite] }));
				} else if (selectedGroup !== 'ALL SITES' && selectedGroup !== 'LOADING') {
					const matchedGroup = result.data.find((g: any) => g.name === selectedGroup);
					dispatch(setSitesInformation(matchedGroup ?? { name: 'ALL SITES' }));
				} else {
					dispatch(setSitesInformation({ name: 'ALL SITES' }));
				}
				await saveTestResults(
					`groupsInfo`,
					result.data,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				await saveTestResults(
					`sitesInfo`,
					sites,
					localStorage.getItem('currentUser') ?? 'unknown'
				);
				if (selectedGroup === 'LOADING') {
					setSelectedGroup('ALL SITES');
				}
			} catch (error) {
				console.log(error);
			}
		} else if (!navigator.onLine) {

			//Try getting the group info from cache
			let groupsData = await getTestResults(
				`groupsInfo`,
				localStorage.getItem('currentUser') ?? 'unknown'
			);

			const sites = deduplicateSites(
				groupsData.flatMap((group: any) => group.sites ?? [])
			);

			setGroups(groupsData);
			setSites(sites);
			renderGroupOptions(groupsData);
			dispatch(setGroupsInformation(groupsData));
			if (selectedSite) {
				dispatch(setSitesInformation({ name: selectedSite.name, sites: [selectedSite] }));
			} else if (selectedGroup !== 'ALL SITES' && selectedGroup !== 'LOADING') {
				const matchedGroup = groupsData.find((g: any) => g.name === selectedGroup);
				dispatch(setSitesInformation(matchedGroup ?? { name: 'ALL SITES' }));
			} else {
				dispatch(setSitesInformation({ name: 'ALL SITES' }));
			}
			if (selectedGroup === 'LOADING') {
				setSelectedGroup('ALL SITES');
			}
		}
		dispatch(setUpdateReduxData(false));
	};
	useEffect(() => {
		if(!navigator.onLine){
			//getGroups(viewedUserId); //this was causing an infinite loop in redux updates.
		}
	}, [reduxGroupsState]);

	useEffect(() => {
		if (!groupsReady) return;
		const fetchData = async () => {
			await getGroups(viewedUserId);
			await getAllUsers();
		};
		fetchData();
	}, [userId, isDataUpdated, orgId, syncedTimeStamps, groupsReady]);

	useEffect(() => {
		if (!groups)
			return;
		if (sites){
				
		}
			const savedGroup =
				localStorage.getItem('selectedGroup') ?? 'ALL SITES';
			if (groups.length > 0 && selectedGroup !== 'LOADING') {
				const groupData = getSelectedGroup(savedGroup);
				if (groupData?.sites) {
					const sortedSites = [...groupData.sites].sort(
						(a, b) => a.sortOrder - b.sortOrder
					);

					const updatedGroupData = {
						...groupData,
						sites: sortedSites,
					};
					dispatch(setSitesInformation(updatedGroupData));
				}
		}
	}, [groups]);
	
	useEffect(() => {
		if (!sites.length) return;
		if (siteRestoredRef.current) return;
		const savedSiteName = localStorage.getItem('selectedSite');
		if (!savedSiteName) return;
		const match = sites.find(s => s.name === savedSiteName);
		if (match) {
			siteRestoredRef.current = true;
			handleSiteChange(match);
		} else {
			siteRestoredRef.current = true;
			localStorage.removeItem('selectedSite');
			handleSiteClear();
		}
	}, [sites]);


	// change of user load different groups based on the user info

	const [isMobile, setIsMobile] = useState(false);
	const [browserClass, setBrowserClass] = useState('');
	const [osClass, setOSClass] = useState('');
  
	useEffect(() => {
		// Check if the screen width is smaller than 1024px to detect mobile
		const checkDeviceType = () => {
			const screenWidth = window.innerWidth;
			setIsMobile(screenWidth < 1024);
		};

		// Function to detect browser and operating system
		const detectBrowserAndOS = () => {
			const userAgent = navigator.userAgent;

			// Detect browser: set class if NOT Chrome or Edge
			if (userAgent.indexOf('Chrome') === -1 && userAgent.indexOf('Edge') === -1) {
				setBrowserClass('unsupported-browser'); // Use a class for non-Chrome/Edge
			}

			// Detect operating system: set class if NOT Windows
			if (userAgent.indexOf('Win') === -1) {
				setOSClass('unsupported-os'); // Use a class for non-Windows OS
			}
		};

		checkDeviceType();
		detectBrowserAndOS();
		if (!hasLogged.current && !window.location.href.includes("reports")){
			hasLogged.current = true;
		}
		// Add event listener for window resize to re-check device type
		window.addEventListener('resize', checkDeviceType);

		// Clean up the event listener
		return () => window.removeEventListener('resize', checkDeviceType);
	}, []);

	return (
		<>

			{isImportOpen && (
				<ImportModal
					onClose={closeImportModal}
				/>
			)}

			<AppBar
				className={`${className} ${'web-app'}`}//Capacitor.isNativePlatform() ? 'mobile-app' : 'web-app'}`}
				position="static"
				sx={{
					bgcolor: 'white',
					boxShadow: 0,
					borderBottom: 1,
					borderColor: '#E5E7EB',
					height: '52px',
					boxSizing: 'border-box',
				}}
				style={{ height: '52px' }}
				version-info={process.env.REACT_APP_ASSIGNED_VERSION}
				is-mobile={'false'}//Capacitor.isNativePlatform() ? 'true' : 'false'}
			>
				<Container maxWidth={false}>
					<Toolbar
						disableGutters
						className="gap-4 flex items-center"
						style={{ minHeight: '52px', height: '52px' }}
					>
						<img
							src="/assets/logos/relay.svg"
							alt="LuminUltra Relay"
							style={{ height: '43px', paddingTop:'5px', paddingBottom: '6px'}}
						/>

						<Text addStyle="!font-inter !text-[14px] !ml-2 text-primary-grey md:block hidden company" sx={{fontWeight: "600"}}>
							{window.location.href.includes("reports") ? "Lab Reports" : "ATP Testing"}
						</Text>
						<Line addStyle="h-[52px]" />

						<Text addStyle={`!font-inter !text-[14px] !text-primary-grey mr-2 ml-2 username ${orgName} 
						${isMobile ? 'mobile-device' : 'desktop-device'} 
						${browserClass} ${osClass}`}
						>
							{userData?.firstName} {userData?.lastName}
						</Text>
						<Box sx={{ flexGrow: 1 }}></Box>

						{!window.location.href.includes("reports") && (
							!isConnected ? (
								<Button onClick={handleConnectToPort} style={{ textTransform: 'none' }} >
									<span className="md:flex hidden items-center">
										<img
										src={`/assets/icons/${isConnected ? 'Usb.svg' : 'offline.svg'}`}
										alt={`${
											isConnected ? 'Connected' : 'Offline'
										} Icon`}
										className="w-5 h-5 shrink-0"
										/>
										<span
										className="!font-jost !text-[#2C2C2C] !text-[16px] ml-1"
										style={{ lineHeight: '1.1' }}
										id={isUsbToastVisible ? "connection-error" : ""}
										>
										Device{' '}
										<span className="!font-jost !text-[#2C2C2C] !text-[16px] lowercase pbm-status">
											{isConnected
												? 'connected'
												: 'not connected'}
										</span>
										</span>
									</span>
								</Button>
							) : (
								<div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
									<Button onClick={handleConnectToPort} style={{ textTransform: 'none', width: 'fit-content', minWidth: 0 }}>
										<span className="md:flex hidden items-center">
											<img
												src={`/assets/icons/${
														isConnected ? 'Usb.svg' : 'offline.svg'
													}`}
													alt={`${
														isConnected ? 'Connected' : 'Offline'
													} Icon`}
												className="w-5 h-5 shrink-0"
											/>
										<span
											className="!font-jost !text-[#2C2C2C] !text-[16px] ml-2"
											style={{ lineHeight: '1.1' }}
										>
											{isAnalyzerConnected ? 'Analyzer' : 'PBM'}{' '}
											<span className="!font-jost !text-[#2C2C2C] !text-[16px] pbm-status">
											{isConnected ? 'connected' : 'not connected'}
											</span>
										</span>
										</span>
									</Button>
									<Button
										onClick={openImportModal}
										style={{
										display: isConnected && isAnalyzerConnected ? 'inline-block' : 'none',
										padding: 1,
										minWidth: 'auto',
										width: 'auto',
										border: 'none',
										background: 'none',
										}}
									>
										<img
												src={`/assets/icons/${
													isConnected ? 'Export2.svg' : 'offline.svg'
												}`}
												alt={`${
													isConnected ? 'Import' : 'Offline'
												} Icon`}
												className="w-5 h-5 md:block hidden"
											/>
									</Button>
								</div>
							)
						)}
						
						{!window.location.href.includes("reports") && (
							(() => {
								if (!navigator.onLine || !userData?.userPermissionVOs || userData?.userPermissionVOs[0]?.level === 3) {
									return <></>
								} else if (userOptions && isAllSitesLoaded.allSitesLoaded) {
									return (
										<>
											<Line addStyle="h-[52px]" />
											<Text addStyle="!font-inter !text-[14px] !text-primary-grey  ml-2 !mr-[-20px]">
												User:
											</Text>
											<DropdownSelectUsers
												value={selectedUser.id}
												onChange={handleChangeUser}
												options={userOptions}
												addStyle="!font-inter !min-w-[120px] !max-w-[250px] !w-[auto] group-dropdown !leading-[23px] !mr-[-15px]"
											/>
										</>
									)
								} else {
									return (
										<>
											<Line addStyle="h-[52px]" />
											<Text addStyle="!font-inter !text-[14px] !text-primary-grey  ml-2 !mr-[-20px]">
												User:
											</Text>
											<div className="text-[14px] !font-semibold text-primary-lu-blue font-inter p-[20px]">
												<button type="button">LOADING</button>
											</div>
										</>
									)
								}
							})()
						)}
						
						{!window.location.href.includes("reports") && (
							(() => {
								return (
									<>
										<Line addStyle="h-[52px]" />
										<Text addStyle="!font-inter !text-[14px] !text-primary-grey  ml-2 !mr-[-20px]">
											Group:
										</Text>
										{groups && isAllSitesLoaded.allSitesLoaded ? (
											<>
												<DropdownSelect
													value={selectedGroup}
													onChange={handleChangeGroup}
													options={groupOptions}
													addStyle="!font-inter !min-w-[120px] !max-w-[250px] !w-[auto] group-dropdown !leading-[23px] !mr-[-15px]"
													showClear={selectedGroup !== 'ALL SITES'}
													onClear={() => {handleChangeGroup('ALL SITES');}}
												/>
											</>
										) : (
											<div className="text-[14px] !font-semibold text-primary-lu-blue font-inter p-[20px]">
												<button type="button">LOADING</button>
											</div>
										)}
									</>)
							})()
						)}
						{!window.location.href.includes("reports") && (
							(() => {
								const siteLabel = selectedSite?.name || 'ALL SITES';
        						const siteInputWidth = siteLabel === 'ALL SITES' ? 125 : Math.max(140, Math.min(200, siteLabel.length * 7.8 + 65));
								return (
									<>
										<Line addStyle="h-[52px]" />
										<Text addStyle="!font-inter !text-[14px] !text-primary-grey  ml-2 !mr-[-20px]">
											Site:
										</Text>
										{groups && isAllSitesLoaded.allSitesLoaded ? (
											<Tooltip title={selectedSite?.name ?? ''}  placement="top-start" 
												PopperProps={{ modifiers: [ {
													name: 'offset',
													options: {
														offset: [10, -13],
													},
												},],
											}}>
											<Autocomplete
												value={selectedSite}
												disableClearable={false}
												onChange={(_, option) => (!option || option.siteId === null) ? handleSiteClear() : handleSiteChange(option)}
												options={[...getFilteredSites(), { siteId: null, id: null, name: 'ALL SITES' }]}
												getOptionLabel={(option) => option?.name ?? ''}
												
												isOptionEqualToValue={(option, value) => {
													if (option.siteId === null && value.siteId === null) return true;
													return (option?.siteId ?? option?.id) === (value?.siteId ?? value?.id);
												}}
												className="!font-inter group-dropdown !mr-[-15px]"
                        						style={{ width: siteInputWidth, transition: 'width 0.15s ease' }}
												size="small"
												renderOption={(props, option) => {
													const { key, ...restProps } = props;
													return (
														<li
															key={option.siteId ?? option.id ?? 'all-sites'}
															{...restProps}
															style={{
																opacity: 1,
																outline: 'none',
																fontFamily: 'Inter, sans-serif',
															}}
														>
															{option?.name}
														</li>
													);
												}}
												
												renderInput={(params) => (
													<TextField
														{...params}
														size="small"
														placeholder="ALL SITES"
														className="!font-inter !leading-[23px]"
														sx={{
															'& .MuiOutlinedInput-notchedOutline': { //remove border effects
																border: 'none',
															},
															'&:hover .MuiOutlinedInput-notchedOutline': {
																border: 'none',
															},
															'& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
																border: 'none',
															},
															'& .MuiAutocomplete-endAdornment': { //fixes some spacing issues b/w the item and the ClearSelection X
																position: 'absolute',
																right: '4px',
																display: 'flex',
																alignItems: 'center',
																gap: '0px',
															},
															'& .MuiInputBase-root': {
																paddingRight: selectedSite ? 
																	(siteInputWidth >= 170) ? '56px !important'  
																	:'52px !important' 
																: '28px !important',
															},
															'& .MuiInputBase-input': {
																fontFamily: 'Inter, sans-serif',
																fontSize: '14px',
																color: CONSTANTS.PRIMARY_COLOR_BLUE,
																fontWeight: 600,
															},
															'& .MuiInputBase-input::placeholder': { //ALL SITES case
																textTransform: 'uppercase',
																opacity: 1,
															},
															'& .MuiAutocomplete-clearIndicator': { //Always show the clear indicator unless ALL SITES is selected
																display: selectedSite ? 'flex' : 'none',
																visibility: 'hidden',
																left: '-2px'

															},
															'&:hover .MuiAutocomplete-clearIndicator': {
																visibility: 'visible',
															},
															'& .MuiAutocomplete-popupIndicator': {
																marginRight: '-2px',
															},
														}}
													/>
												)}
											/>
											</Tooltip>
										) : (
											<div className="text-[14px] !font-semibold text-primary-lu-blue font-inter p-[20px]">
												<button type="button">LOADING</button>
											</div>
										)}
										<Line addStyle="h-[52px]" />
									</>)
							})()
						)}
						<Tooltip title="Main menu">
							<IconButton
								className="open-menu"
								onClick={handleOpenUserMenu}
								sx={{ p: 0 }}
							>
								<img
									src="/assets/icons/drawer.svg"
									alt="Drawer Icon"
									className="md:block hidden"
									style={{ width: '30px', height: '38px' }}
								/>
							</IconButton>
						</Tooltip>
						<UserDropdown
							anchorEl={anchorElUser}
							onClose={handleCloseUserMenu}
							userName={`${userData?.firstName} ${userData?.lastName}`}
							user={user}
							handleConnectToPort={handleConnectToPort}
							isConnected={isConnected}
							isAnalyzerConnected={isAnalyzerConnected}
						/>
					</Toolbar>
				</Container>
			</AppBar>
		</>
	);
};

export default AppNav;
